// Reactive (Vue.observable-based) sidebar state, shared between TopBar.vue
// (the toggle button) and SideBar.vue/TemplateDashboard.vue (which react to it), the
// same cross-component pattern modalStore.js uses.
//
// Two modes, split at Tailwind's `lg` breakpoint (1024px):
// - Desktop (>= lg): the sidebar is always visible and can be collapsed to icons.
// - Mobile/tablet (< lg): the sidebar is an off-canvas drawer, hidden by default.
import Vue from 'vue';

const STORAGE_KEY = 'sidebarCollapsed';
const DESKTOP_QUERY = '(min-width: 1024px)';

const desktopMedia = window.matchMedia(DESKTOP_QUERY);

const state = Vue.observable({
    collapsed: localStorage.getItem(STORAGE_KEY) === 'true',
    // True while the mouse is hovering a collapsed sidebar, temporarily previewing
    // it at full width. Kept separate from `collapsed` (the locked toggle state, the
    // one persisted to localStorage) so the preview never overwrites the user's choice.
    hovering: false,
    // Mobile drawer visibility (only meaningful below the `lg` breakpoint).
    mobileOpen: false,
    isDesktop: desktopMedia.matches,
});

desktopMedia.addEventListener('change', (event) => {
    state.isDesktop = event.matches;
    state.mobileOpen = false;
    state.hovering = false;
});

export function toggleSidebar() {
    state.collapsed = !state.collapsed;
    localStorage.setItem(STORAGE_KEY, state.collapsed);
}

// Single entry point for the header's menu button: collapses/expands the sidebar on
// desktop, opens/closes the drawer on mobile.
export function toggleSidebarResponsive() {
    if (state.isDesktop) {
        toggleSidebar();
    } else {
        state.mobileOpen = !state.mobileOpen;
    }
}

export function closeMobileSidebar() {
    state.mobileOpen = false;
}

export function isMobileSidebarOpen() {
    return state.mobileOpen;
}

export function isDesktopViewport() {
    return state.isDesktop;
}

export function isSidebarCollapsed() {
    return state.collapsed;
}

export function setSidebarHovering(value) {
    state.hovering = state.isDesktop && value;
}

// What the sidebar's own width/labels and the header's offset should follow:
// collapsed, unless a hover preview is currently expanding it. The mobile drawer is
// never collapsed.
export function isSidebarEffectivelyCollapsed() {
    return state.isDesktop && state.collapsed && !state.hovering;
}
